import {
  AlertTriangle,
  Banknote,
  BriefcaseBusiness,
  CalendarDays,
  CalendarRange,
  Circle,
  Eye,
  Info,
  PenLine,
  Star,
  UserRound,
  type LucideIcon
} from "lucide-react";
import type { FC, ReactNode } from "react";

import { ListCard } from "@/components/ui/list-card";
import { MetaLine } from "@/components/ui/meta-line";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import {
  exhibitName,
  presentFileCard,
  titleCase,
  type FileCardRow,
  type FileCardSection
} from "./fileCardSummary";

type Props = {
  markdown: string;
  exhibitLabel?: string | null;
  fallbackTitle?: string;
  /** When the card was built (ISO). */
  builtAt?: string | null;
  /** The model that built it, shown only on hover. */
  model?: string | null;
  stale?: boolean;
  /**
   * The file's own category and type, editable. It replaces the card's
   * Document Type and Filed Under pairs, which are read from the same metadata.
   */
  classification?: ReactNode;
};

/** The at-a-glance pairs `classification` stands in for. */
const CLASSIFICATION_PAIRS = new Set(["Document Type", "Filed Under"]);

const KIND_ICON: Record<string, LucideIcon> = {
  party: UserRound,
  name: UserRound,
  role: BriefcaseBusiness,
  date: CalendarDays,
  period: CalendarRange,
  amount: Banknote,
  significance: Star,
  fact: Info
};

const summarizedOn = (builtAt: string | null | undefined) => {
  if (!builtAt) return null;
  const at = new Date(builtAt);
  if (Number.isNaN(at.getTime())) return null;
  return `Summarized ${at.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  })}`;
};

/** Past this, an at-a-glance value takes a full row. */
const LONG_VALUE = 36;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * One group of the card: a tray heading over its rows, in one ListCard. A
 * warn section takes the RFE Must Rebut treatment (soft tone, toned title).
 */
const Section: FC<{
  title: string;
  meta?: string | null;
  tone?: "neutral" | "warn";
  testId?: string;
  children: ReactNode;
}> = ({ title, meta, tone = "neutral", testId, children }) => (
  <section data-testid={testId}>
    <ListCard tone={tone}>
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-2",
          tone === "warn" ? "bg-warn-soft" : "bg-fill-1"
        )}
      >
        {tone === "warn" ? (
          <AlertTriangle className="size-3.5 shrink-0 text-warn" aria-hidden />
        ) : null}
        <h2
          className={cn(
            "min-w-0 text-base font-medium",
            tone === "warn" ? "text-warn" : "text-fg"
          )}
        >
          {title}
        </h2>
        {meta ? <span className="text-sm text-fg-2">{meta}</span> : null}
      </div>
      {children}
    </ListCard>
  </section>
);

const PageTag: FC<{ page: string | null }> = ({ page }) =>
  page ? <Tag className="tabular-nums">{page}</Tag> : null;

const supportsLine = (supports: string[]) =>
  supports.length ? `Supports ${supports.join(", ")}` : null;

/** One field: kind icon, label, value with its detail, and the page when it helps. */
const FieldRow: FC<{ item: FileCardRow; showPage: boolean }> = ({
  item,
  showPage
}) => {
  const Icon = (item.kind && KIND_ICON[item.kind]) || Circle;
  return (
    <div
      className="flex items-start gap-3 px-4 py-2.5"
      data-testid="card-field"
    >
      <span
        className="mt-0.5 flex shrink-0 text-fg-3"
        title={item.kindLabel ?? undefined}
      >
        <Icon
          className={cn("size-3.5", !item.kind && "text-fg-4")}
          aria-hidden
        />
        {item.kindLabel ? (
          <span className="sr-only">{item.kindLabel}</span>
        ) : null}
      </span>
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-0.5 @md/card:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
        {item.label ? (
          <span className="text-base break-words text-fg-2">{item.label}</span>
        ) : null}
        <div
          className={cn(
            "flex min-w-0 flex-col",
            !item.label && "@md/card:col-span-2"
          )}
        >
          <span className="text-base break-words text-fg">{item.value}</span>
          {item.detail ? (
            <span className="text-sm break-words text-fg-2">{item.detail}</span>
          ) : null}
        </div>
      </div>
      {showPage ? <PageTag page={item.page} /> : null}
    </div>
  );
};

const unquote = (text: string) => {
  const match = /^"([\s\S]*)"$/.exec(text);
  return match ? match[1] : text;
};

const QuoteRow: FC<{ item: FileCardRow; showPage: boolean }> = ({
  item,
  showPage
}) => (
  <div className="flex flex-col gap-1.5 px-4 py-3" data-testid="card-quote">
    <blockquote className="border-l border-line-3 pl-3 text-base text-fg">
      “{unquote(item.value)}”
    </blockquote>
    {item.key || (showPage && item.page) || item.supports.length ? (
      <div className="flex flex-wrap items-center gap-1.5 pl-3">
        {item.key ? <StatusBadge tone="info">Key Quote</StatusBadge> : null}
        {showPage ? <PageTag page={item.page} /> : null}
        <MetaLine className="flex-wrap" parts={[supportsLine(item.supports)]} />
      </div>
    ) : null}
  </div>
);

/** A concern reads like an RFE flag: warn icon, the text, where and why. */
const ConcernRow: FC<{ item: FileCardRow; showPage: boolean }> = ({
  item,
  showPage
}) => (
  <div className="flex items-start gap-2 px-4 py-3" data-testid="card-concern">
    <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warn" aria-hidden />
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-base break-words text-fg">{item.value}</span>
      <MetaLine className="flex-wrap" parts={[supportsLine(item.supports)]} />
    </div>
    {showPage ? <PageTag page={item.page} /> : null}
  </div>
);

const PlainSection: FC<{ section: FileCardSection; showPage: boolean }> = ({
  section,
  showPage
}) => (
  <Section title={section.title} meta={section.note}>
    {section.rows.map((item, i) => (
      <FieldRow key={i} item={item} showPage={showPage} />
    ))}
  </Section>
);

/**
 * A file card laid out for an attorney, in the Notice Brief's language: a
 * summary card with the document at a glance, concerns first in a warn
 * card, then the fields grouped under what they support, the quotes, and
 * what to check on the page. `markdown` is the stored card and is only read.
 */
export const FileCardSummaryView: FC<Props> = ({
  markdown,
  exhibitLabel,
  fallbackTitle,
  builtAt,
  model,
  stale = false,
  classification
}) => {
  const card = presentFileCard(markdown);
  const heading = card.title || fallbackTitle || "File Card";
  const exhibit = exhibitName(exhibitLabel);
  const showPage = card.pageCount !== 1;
  const pairs = [
    ...card.glance.filter(
      (pair) => !classification || !CLASSIFICATION_PAIRS.has(pair.label)
    ),
    ...(card.pageCount
      ? [{ label: "Pages", value: String(card.pageCount) }]
      : [])
  ];
  const isLong = (value: string) => value.length > LONG_VALUE;
  const glance = [
    ...pairs.filter((pair) => !isLong(pair.value)),
    ...pairs.filter((pair) => isLong(pair.value))
  ];
  const summarized = summarizedOn(builtAt);
  const singleGroup = card.fieldGroups.length === 1;
  const concernCount = card.concerns?.rows.length ?? 0;

  return (
    <article
      data-testid="file-card-summary"
      className="@container/card flex flex-col gap-5"
    >
      <ListCard aria-label={`${heading} card`}>
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-1">
            <MetaLine parts={[exhibit ? `Exhibit ${exhibit}` : null]} />
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-medium text-fg">{heading}</h1>
              {concernCount ? (
                <StatusBadge tone="warn" dot>
                  {concernCount} {concernCount === 1 ? "Concern" : "Concerns"}
                </StatusBadge>
              ) : null}
              {stale ? (
                <StatusBadge
                  tone="warn"
                  title="Built before the case's visa or workflow changed. The next draft rebuilds it."
                >
                  Outdated
                </StatusBadge>
              ) : null}
            </div>
          </div>
          {classification}
          {glance.length ? (
            <dl
              className="grid grid-cols-2 gap-x-6 gap-y-2.5 rounded-lg bg-fill-1 p-3 @md/card:grid-cols-3"
              data-testid="card-glance"
            >
              {glance.map((pair) => (
                <div
                  key={pair.label}
                  className={cn(
                    "flex min-w-0 flex-col",
                    isLong(pair.value) && "col-span-full"
                  )}
                >
                  <dt className="text-sm text-fg-2">{pair.label}</dt>
                  <dd className="text-base break-words text-fg">
                    {pair.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {card.about.map((bit) => (
            <p key={bit} className="text-base text-fg">
              {bit}
            </p>
          ))}
        </div>
        {card.summary ? (
          <div className="flex flex-col gap-1 px-4 py-3">
            <h2 className="text-base font-medium text-fg">Summary</h2>
            <p className="text-md text-fg">{card.summary}</p>
          </div>
        ) : null}
        {card.corrections.map((correction, i) => (
          <div
            key={i}
            className="flex items-start gap-2 px-4 py-3"
            data-testid="card-correction"
          >
            <PenLine
              className="mt-0.5 size-3.5 shrink-0 text-fg-3"
              aria-hidden
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-base break-words text-fg">
                {correction.text}
              </span>
              <MetaLine parts={["Attorney Correction", correction.by]} />
            </div>
          </div>
        ))}
        {card.pagesRead ? (
          <div className="flex items-start gap-2 px-4 py-3">
            <Info className="mt-0.5 size-3.5 shrink-0 text-fg-3" aria-hidden />
            <span className="text-base text-fg">
              {card.pagesRead} were read into this card; the rest of the file
              was not.
            </span>
          </div>
        ) : null}
      </ListCard>

      {card.concerns?.rows.length ? (
        <Section
          title="Concerns"
          meta={card.concerns.note}
          tone="warn"
          testId="card-concerns"
        >
          {card.concerns.rows.map((item, i) => (
            <ConcernRow key={i} item={item} showPage={showPage} />
          ))}
        </Section>
      ) : null}

      {card.fieldGroups.map((group) => (
        <Section
          key={group.supports.join("|") || "none"}
          title={
            group.supports.length
              ? `Supports ${group.supports.map(titleCase).join(" · ")}`
              : singleGroup
              ? "Fields"
              : "Other Fields"
          }
          meta={plural(group.rows.length, "field")}
          testId="card-field-group"
        >
          {group.rows.map((item, i) => (
            <FieldRow key={i} item={item} showPage={showPage} />
          ))}
        </Section>
      ))}

      {card.quotes?.rows.length ? (
        <Section title="Quotes" meta={card.quotes.note} testId="card-quotes">
          {card.quotes.rows.map((item, i) => (
            <QuoteRow key={i} item={item} showPage={showPage} />
          ))}
        </Section>
      ) : null}

      {card.lookAt.length ? (
        <Section title="Check on the Page">
          {card.lookAt.map((item, i) => (
            <div key={i} className="flex items-start gap-2 px-4 py-3">
              <Eye className="mt-0.5 size-3.5 shrink-0 text-fg-3" aria-hidden />
              <span className="min-w-0 flex-1 text-base text-fg">
                {item.text}
              </span>
              <PageTag page={item.page} />
            </div>
          ))}
        </Section>
      ) : null}

      {card.sections.map((section) =>
        section.rows.length ? (
          <PlainSection
            key={section.title}
            section={section}
            showPage={showPage}
          />
        ) : null
      )}

      {card.notes.length ? (
        <Section title="Notes">
          {card.notes.map((note, i) => (
            <p key={i} className="px-4 py-3 text-base text-fg">
              {note}
            </p>
          ))}
        </Section>
      ) : null}

      <footer className="flex flex-col gap-0.5 px-1">
        {summarized ? (
          <span
            className="text-sm text-fg-3"
            title={model ? `Built by ${model}` : undefined}
          >
            {summarized}
          </span>
        ) : null}
        {card.footnotes.length ? (
          <span className="text-sm break-all text-fg-3">
            {card.footnotes.join(" · ")}
          </span>
        ) : null}
      </footer>
    </article>
  );
};
